import {
  AgreementApi,
  AgreementIssuance,
  AgreementRecord,
  AgreementStatus,
  AgreementVersion,
} from '../types/driverAgreements'

const unavailable = () => new Error('DRIVER_AGREEMENT_UNAVAILABLE')
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object'
const integer = (value: unknown) =>
  Number.isSafeInteger(value) && Number(value) > 0
const date = (value: unknown) =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T.*Z$/.test(value) &&
  Number.isFinite(Date.parse(value))
const nullableDate = (value: unknown) => value === null || date(value)
const urlPair = (url: unknown, expires: unknown) => {
  if (url === null) return expires === null
  if (typeof url !== 'string' || !date(expires)) return false
  try {
    const parsed = new URL(url)
    return (
      !parsed.username &&
      !parsed.password &&
      (parsed.protocol === 'https:' ||
        (parsed.protocol === 'http:' &&
          ['[::1]', '127.0.0.1', 'localhost'].includes(parsed.hostname)))
    )
  } catch {
    return false
  }
}
const version = (value: unknown): value is AgreementVersion =>
  object(value) &&
  integer(value.id) &&
  integer(value.series_id) &&
  integer(value.version) &&
  typeof value.template_sha256 === 'string' &&
  /^[a-f0-9]{64}$/.test(value.template_sha256)

export const agreementUid = (user: unknown): string =>
  object(user) && typeof user.uid === 'string' ? user.uid : ''

export type AgreementHistory = {
  lastSeries?: number
  records: Record<number, AgreementRecord>
  uid: string
}
export const rememberAgreement = (
  history: AgreementHistory,
  uid: string,
  record?: AgreementRecord
): AgreementHistory => {
  const current: AgreementHistory =
    history.uid === uid ? history : { records: {}, uid }
  if (!uid || !record?.accepted_at) return current
  const series = record.version.series_id
  const previous = current.records[series]
  if (
    previous &&
    (previous.version.version > record.version.version ||
      (previous.version.version === record.version.version &&
        (previous.id !== record.id ||
          previous.version.id !== record.version.id ||
          previous.version.template_sha256 !== record.version.template_sha256)))
  )
    return current
  return {
    lastSeries: series,
    records: { ...current.records, [series]: record },
    uid,
  }
}
export const acceptedAgreement = (
  history: AgreementHistory,
  uid: string,
  status?: AgreementStatus
): AgreementRecord | null => {
  if (!uid) return null
  const cached = status?.highest_accepted_record
  const series =
    status?.required_version?.series_id ??
    cached?.version.series_id ??
    (history.uid === uid ? history.lastSeries : undefined)
  if (series === undefined) return null
  const known = history.uid === uid ? history.records[series] : undefined
  const matching = cached?.version.series_id === series ? cached : null
  return known &&
    (!matching || known.version.version >= matching.version.version)
    ? known
    : matching
}

export const parseAgreementRecord = (value: unknown): AgreementRecord => {
  if (
    !object(value) ||
    !integer(value.id) ||
    !version(value.version) ||
    !date(value.issued_at) ||
    !nullableDate(value.presented_at) ||
    !nullableDate(value.accepted_at) ||
    !['failed', 'not_requested', 'pending', 'ready'].includes(
      String(value.render_status)
    ) ||
    !(
      value.render_error_code === null ||
      typeof value.render_error_code === 'string'
    ) ||
    !urlPair(value.preview_url, value.preview_expires_at) ||
    !urlPair(value.signed_copy_url, value.signed_copy_expires_at) ||
    (value.accepted_at === null && value.render_status !== 'not_requested') ||
    (value.accepted_at !== null && value.render_status === 'not_requested') ||
    (value.render_status === 'ready' &&
      (value.accepted_at === null || value.signed_copy_url === null)) ||
    (value.render_status !== 'ready' && value.signed_copy_url !== null)
  )
    throw unavailable()
  return value as AgreementRecord
}

export const parseAgreementStatus = (value: unknown): AgreementStatus => {
  if (
    !object(value) ||
    !['disabled', 'enabled', 'out_of_scope'].includes(String(value.mode)) ||
    value.enforcement !== 'next_session' ||
    typeof value.requires_acceptance !== 'boolean' ||
    !(value.required_version === null || version(value.required_version))
  )
    throw unavailable()
  const required = value.required_version as AgreementVersion | null
  const accepted =
    value.highest_accepted_record === null
      ? null
      : parseAgreementRecord(value.highest_accepted_record)
  const pending =
    value.pending_required_record === null
      ? null
      : parseAgreementRecord(value.pending_required_record)
  if (
    (accepted && !accepted.accepted_at) ||
    (pending && pending.accepted_at) ||
    (value.mode === 'enabled' && !required) ||
    (value.mode !== 'enabled' && (required || value.requires_acceptance)) ||
    (pending &&
      (!required ||
        pending.version.id !== required.id ||
        pending.version.series_id !== required.series_id ||
        pending.version.version !== required.version ||
        pending.version.template_sha256 !== required.template_sha256)) ||
    (value.mode === 'enabled' &&
      value.requires_acceptance !==
        !(
          accepted &&
          required &&
          accepted.version.series_id === required.series_id &&
          accepted.version.version >= required.version
        ))
  ) {
    throw unavailable()
  }
  return {
    ...value,
    highest_accepted_record: accepted,
    pending_required_record: pending,
  } as AgreementStatus
}

export const parseAgreementIssuance = (value: unknown): AgreementIssuance => {
  if (!object(value) || !['accept', 'view'].includes(String(value.action)))
    throw unavailable()
  const record = parseAgreementRecord(value.record)
  if ((value.action === 'view') !== Boolean(record.accepted_at))
    throw unavailable()
  return { action: value.action as AgreementIssuance['action'], record }
}

export const agreementChanged = (error: unknown) =>
  object(error) &&
  object(error.response) &&
  error.response.status === 409 &&
  object(error.response.data) &&
  error.response.data.customError === 'DRIVER_AGREEMENT_CHANGED'

export const canUseLegacyAgreement = (
  status: AgreementStatus | undefined,
  failed: boolean,
  fetching: boolean
) =>
  !failed &&
  !fetching &&
  ['disabled', 'out_of_scope'].includes(status?.mode ?? '')

export type AgreementFormState = {
  busy: boolean
  checked: boolean
  displayed: boolean
  error: 'changed' | 'unavailable' | null
  load: number
  record: AgreementRecord | null
}
export const emptyAgreementState = (): AgreementFormState => ({
  busy: false,
  checked: false,
  displayed: false,
  error: null,
  load: 0,
  record: null,
})
export const canSubmitAgreement = (state: AgreementFormState) =>
  Boolean(
    state.record &&
      !state.record.accepted_at &&
      state.displayed &&
      state.checked &&
      !state.busy
  )
export const shouldPollAgreement = (
  record: AgreementRecord | null,
  foreground: boolean,
  reads: number,
  elapsed = 0
) =>
  Boolean(
    record?.accepted_at &&
      record.render_status === 'pending' &&
      foreground &&
      reads < 12 &&
      elapsed <= 60000
  )

// Every operation belongs to one form lifetime. No callback can mutate a later issuance.
export class AgreementFlow {
  private accepting = false

  private api: AgreementApi

  private changed: (state: AgreementFormState) => void

  private controller = new AbortController()

  private disposed = false

  private epoch = 0

  private known: AgreementRecord | undefined

  private readController: AbortController | null = null

  private urlRefreshes = 0

  state = emptyAgreementState()

  constructor(api: AgreementApi, changed: (state: AgreementFormState) => void) {
    this.api = api
    this.changed = changed
  }

  private current(epoch: number) {
    return !this.disposed && this.epoch === epoch
  }

  private fail(error: unknown) {
    if (agreementChanged(error) && !this.state.record?.accepted_at) {
      this.epoch += 1
      this.controller.abort()
      this.controller = new AbortController()
      this.cancelRead()
      this.accepting = false
      this.update({
        ...emptyAgreementState(),
        error: 'changed',
        load: this.state.load + 1,
      })
    } else {
      this.update({ busy: false, checked: false, error: 'unavailable' })
    }
  }

  private same(record: AgreementRecord, expected: AgreementRecord) {
    if (
      record.id !== expected.id ||
      record.issued_at !== expected.issued_at ||
      (expected.accepted_at !== null &&
        (record.accepted_at !== expected.accepted_at ||
          record.presented_at !== expected.presented_at)) ||
      record.version.id !== expected.version.id ||
      record.version.series_id !== expected.version.series_id ||
      record.version.version !== expected.version.version ||
      record.version.template_sha256 !== expected.version.template_sha256
    )
      throw unavailable()
    const valid = (expires: null | string) =>
      expires && Date.parse(expires) > Date.now()
    return {
      ...record,
      ...(valid(expected.preview_expires_at)
        ? {
            preview_expires_at: expected.preview_expires_at,
            preview_url: expected.preview_url,
          }
        : {}),
      ...(valid(expected.signed_copy_expires_at) &&
      record.render_status === 'ready'
        ? {
            signed_copy_expires_at: expected.signed_copy_expires_at,
            signed_copy_url: expected.signed_copy_url,
          }
        : {}),
    }
  }

  private update(patch: Partial<AgreementFormState>) {
    if (!this.disposed) {
      this.state = { ...this.state, ...patch }
      this.changed(this.state)
    }
  }

  cancelRead() {
    if (this.readController) {
      this.readController.abort()
      this.readController = null
      this.update({ busy: false })
    }
  }

  check(checked: boolean) {
    this.update({
      checked: checked && this.state.displayed && !this.state.busy,
    })
  }

  displayed(id: number, hash: string, load: number) {
    const { record } = this.state
    if (
      this.disposed ||
      !record ||
      record.id !== id ||
      record.version.template_sha256 !== hash ||
      this.state.load !== load ||
      record.accepted_at ||
      this.state.displayed ||
      this.state.busy
    )
      return
    this.update({ displayed: true })
  }

  dispose() {
    this.disposed = true
    this.epoch += 1
    this.controller.abort()
    this.cancelRead()
    this.known = undefined
    this.state = { ...emptyAgreementState(), load: this.state.load + 1 }
  }

  async open(known?: AgreementRecord) {
    this.known = known
    this.disposed = false
    this.controller.abort()
    this.controller = new AbortController()
    this.cancelRead()
    this.accepting = false
    this.epoch += 1
    const { epoch } = this
    this.urlRefreshes = 0
    this.update({
      ...emptyAgreementState(),
      busy: true,
      load: this.state.load + 1,
    })
    try {
      if (known) {
        if (!parseAgreementRecord(known).accepted_at) throw unavailable()
        this.update({ record: known })
      }
      const record = known
        ? this.same(
            await this.api.read(known.id, this.controller.signal),
            known
          )
        : (await this.api.issue(this.controller.signal)).record
      if (this.current(epoch)) this.update({ busy: false, record })
    } catch (error) {
      if (this.current(epoch)) this.fail(error)
    }
  }

  async previewFailed() {
    this.update({ checked: false, displayed: false })
    if (this.accepting) return
    if (this.urlRefreshes >= 1) {
      this.update({ error: 'unavailable' })
      return
    }
    this.epoch += 1
    this.controller.abort()
    this.controller = new AbortController()
    this.update({ busy: false })
    this.urlRefreshes += 1
    await this.refresh(true)
  }

  async refresh(renew = false) {
    const { record } = this.state
    const { epoch } = this
    if (!record || this.state.busy) return
    this.update({ busy: true, error: null })
    const readController = new AbortController()
    this.readController = readController
    const cancel = () => readController.abort()
    this.controller.signal.addEventListener('abort', cancel, { once: true })
    try {
      const raw = await this.api.read(record.id, readController.signal)
      const result = this.same(raw, record)
      const changedUrl =
        result.preview_url !== record.preview_url ||
        result.signed_copy_url !== record.signed_copy_url
      if (this.current(epoch) && !readController.signal.aborted)
        this.update({
          busy: false,
          record: renew ? raw : result,
          ...(renew || changedUrl
            ? {
                checked: false,
                displayed: false,
                load: this.state.load + 1,
              }
            : {}),
        })
    } catch (error) {
      if (this.current(epoch) && !readController.signal.aborted)
        this.fail(error)
    } finally {
      this.controller.signal.removeEventListener('abort', cancel)
      if (this.readController === readController) this.readController = null
    }
  }

  async retry() {
    this.urlRefreshes = 0
    if (this.state.record) await this.refresh(true)
    else await this.open(this.known)
  }

  async submit() {
    if (!canSubmitAgreement(this.state)) return
    const record = this.state.record!
    const { epoch } = this
    this.accepting = true
    this.update({ busy: true, error: null })
    try {
      const result = this.same(
        await this.api.accept(
          record.id,
          record.version.template_sha256,
          this.controller.signal
        ),
        record
      )
      if (!result.accepted_at) throw unavailable()
      if (this.current(epoch))
        this.update({
          busy: false,
          checked: false,
          error: null,
          record: result,
        })
    } catch (error) {
      if (!this.current(epoch)) return
      if (agreementChanged(error)) {
        this.fail(error)
      } else {
        try {
          const result = this.same(
            await this.api.read(record.id, this.controller.signal),
            record
          )
          if (this.current(epoch))
            this.update({
              busy: false,
              checked: result.accepted_at ? false : this.state.checked,
              error: result.accepted_at ? null : 'unavailable',
              record: result,
            })
        } catch {
          if (this.current(epoch))
            this.update({ busy: false, error: 'unavailable' })
        }
      }
    } finally {
      if (this.current(epoch)) this.accepting = false
    }
  }
}
